/**
 * FullVSL overlay/CTA safe zones — per aspect ratio (FUL-8).
 *
 * Safe zones define the layout regions later overlay/CTA work targets, so that
 * overlays and CTA surfaces never sit on top of critical video content or the
 * player controls. The position slots match the Phase 0 contract
 * (`OverlayContent.position`) exactly, so an overlay configured with a given
 * position renders inside the matching safe region for whatever aspect ratio
 * the player resolved.
 *
 * Contract reference (FUL-3 §3): OverlayContent.position ∈
 * "top" | "center" | "bottom" | "top_left" | "top_right" |
 * "bottom_left" | "bottom_right".
 */

import {
  type DetectedAspect,
  type Orientation,
  type StandardAspectRatio,
} from "./fullvsl-player";

/** The 7 overlay position slots from the Phase 0 contracts. */
export type OverlayPosition =
  | "top"
  | "center"
  | "bottom"
  | "top_left"
  | "top_right"
  | "bottom_left"
  | "bottom_right";

/** Ordered list of the 7 valid position slots. */
export const OVERLAY_POSITIONS: ReadonlyArray<OverlayPosition> = [
  "top_left",
  "top",
  "top_right",
  "center",
  "bottom_left",
  "bottom",
  "bottom_right",
];

/** Fractional insets (0..1) defining the safe area inside the video frame. */
export interface SafeZoneInsets {
  /** Distance from the top edge, as a fraction of frame height. */
  top: number;
  /** Distance from the right edge, as a fraction of frame width. */
  right: number;
  /** Distance from the bottom edge, as a fraction of frame height. */
  bottom: number;
  /** Distance from the left edge, as a fraction of frame width. */
  left: number;
}

/** A layout region expressed as an absolute fraction rect (0..1) of the frame. */
export interface SafeZoneRegion {
  /** Which overlay position slot this region serves. */
  position: OverlayPosition;
  /** Left offset, 0..1 of frame width. */
  x: number;
  /** Top offset, 0..1 of frame height. */
  y: number;
  /** Width, 0..1 of frame width. */
  width: number;
  /** Height, 0..1 of frame height. */
  height: number;
}

/** Full safe-zone layout for a resolved aspect ratio. */
export interface SafeZoneLayout {
  orientation: Orientation;
  /** Standard label when known, otherwise "custom". */
  label: StandardAspectRatio | "custom";
  /** Frame insets that clear controls and edges. */
  insets: SafeZoneInsets;
  /** Per-position region rects. */
  regions: Record<OverlayPosition, SafeZoneRegion>;
}

/**
 * Per-standard-ratio safe insets. The bottom inset is always larger than top so
 * overlay/CTA content clears the control bar; portrait ratios reserve a taller
 * bottom band because the CTA commonly docks there on vertical video.
 */
const SAFE_ZONES_BY_LABEL: Record<StandardAspectRatio, SafeZoneInsets> = {
  "16:9": { top: 0.06, right: 0.06, bottom: 0.16, left: 0.06 },
  "9:16": { top: 0.1, right: 0.12, bottom: 0.22, left: 0.12 },
  "1:1": { top: 0.08, right: 0.08, bottom: 0.18, left: 0.08 },
  "4:5": { top: 0.09, right: 0.1, bottom: 0.2, left: 0.1 },
  "3:4": { top: 0.09, right: 0.1, bottom: 0.2, left: 0.1 },
};

/** Orientation-derived insets used for unknown/custom ratios. */
const SAFE_ZONES_BY_ORIENTATION: Record<Orientation, SafeZoneInsets> = {
  landscape: SAFE_ZONES_BY_LABEL["16:9"],
  portrait: SAFE_ZONES_BY_LABEL["9:16"],
  square: SAFE_ZONES_BY_LABEL["1:1"],
};

/**
 * Compute the 7 region rects for given insets by splitting the safe inner area
 * into a 3×3 grid. The middle row only exposes a `center` cell (matching the
 * contract position set); the top and bottom rows split into left/center/right.
 */
export function computeRegions(insets: SafeZoneInsets): Record<OverlayPosition, SafeZoneRegion> {
  const clamp = (n: number): number => Math.min(1, Math.max(0, n));
  const left = clamp(insets.left);
  const right = clamp(insets.right);
  const top = clamp(insets.top);
  const bottom = clamp(insets.bottom);

  const innerX = left;
  const innerY = top;
  const innerW = clamp(1 - left - right);
  const innerH = clamp(1 - top - bottom);

  const colW = innerW / 3;
  const rowH = innerH / 3;

  const mk = (position: OverlayPosition, col: number, row: number): SafeZoneRegion => ({
    position,
    x: innerX + col * colW,
    y: innerY + row * rowH,
    width: colW,
    height: rowH,
  });

  // Middle row only exposes the center cell per the contract position set.
  return {
    top_left: mk("top_left", 0, 0),
    top: mk("top", 1, 0),
    top_right: mk("top_right", 2, 0),
    center: mk("center", 1, 1),
    bottom_left: mk("bottom_left", 0, 2),
    bottom: mk("bottom", 1, 2),
    bottom_right: mk("bottom_right", 2, 2),
  };
}

/**
 * Resolve the safe-zone layout for a detected aspect. Standard ratios use their
 * tuned insets; custom/unknown ratios fall back to orientation-derived insets.
 */
export function resolveSafeZones(aspect: DetectedAspect): SafeZoneLayout {
  const { label, orientation } = aspect;
  const insets =
    label !== "custom"
      ? SAFE_ZONES_BY_LABEL[label]
      : SAFE_ZONES_BY_ORIENTATION[orientation];

  return {
    orientation,
    label,
    insets,
    regions: computeRegions(insets),
  };
}

/**
 * Resolve a layout directly from an orientation (no media dimensions needed).
 * Useful for the placeholder frame before metadata arrives.
 */
export function resolveSafeZonesByOrientation(
  orientation: Orientation,
): SafeZoneLayout {
  const insets = SAFE_ZONES_BY_ORIENTATION[orientation];
  return {
    orientation,
    label: orientation === "landscape" ? "16:9" : orientation === "portrait" ? "9:16" : "1:1",
    insets,
    regions: computeRegions(insets),
  };
}
